import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TaskCard from '@/components/TaskCard';
import { GripVertical, Pencil, Plus, Target, Trash2 } from 'lucide-react';
import { focusOpacity, focusBlur } from '@/utils/focusMode';

// Shared column width classes — keep in sync across header + body cells.
const COLUMN_SIZE =
  'flex-shrink-0 w-[calc(100vw-3rem)] sm:w-[420px] sm:min-w-[420px] sm:max-w-[600px]';

// Menu-item hover uses the user's secondary color from settings.
const MENU_ITEM_HOVER =
  'focus:bg-[var(--color-secondary)] focus:text-black data-[highlighted]:bg-[var(--color-secondary)] data-[highlighted]:text-black';

/**
 * SortableTaskItem
 * Wrapper that makes a TaskCard draggable.
 */
function SortableTaskItem({
  task,
  onComplete,
  onUpdate,
  tags,
  dragHandlePosition,
  onMoveToTop,
  onMoveToBottom,
  onMoveUp,
  onMoveDown,
  compact = false,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    data: { type: 'task', activeListId: task.active_list_id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      data-testid="draggable-task-item"
    >
      <TaskCard
        task={task}
        onComplete={onComplete}
        onUpdate={onUpdate}
        showDragHandle
        isDragging={isDragging}
        tags={tags}
        dragHandlePosition={dragHandlePosition}
        onMoveToTop={onMoveToTop}
        onMoveToBottom={onMoveToBottom}
        onMoveUp={onMoveUp}
        onMoveDown={onMoveDown}
        compact={compact}
      />
    </div>
  );
}

/**
 * DroppableListColumn
 * Droppable target for a list column.
 */
function DroppableListColumn({ listId, children, className }) {
  const { setNodeRef, isOver } = useDroppable({
    id: `list-drop-${listId}`,
    data: { type: 'list', listId },
  });
  return (
    <div
      ref={setNodeRef}
      className={`${className || ''} ${isOver ? 'ring-2 ring-primary/60 rounded-lg' : ''}`}
      data-testid={`list-drop-zone-${listId}`}
    >
      {children}
    </div>
  );
}

/**
 * ListHeaderCell
 * A single list column's header (grip + title + context label + kabab).
 * Horizontally sortable via the grip handle.
 * Designed to live inside a separate "sticky headers strip" above the
 * body row so it can freeze at the top of the window without giving
 * each list its own vertical scroll context.
 */
export function ListHeaderCell({
  list,
  listTasks,
  activeListsCount,
  dragHandlePosition = 'right',
  renamingListId,
  renameValue,
  setRenameValue,
  startRenameList,
  commitRenameList,
  setRenamingListId,
  setListToDelete,
  handleAutoPopulate,
  handleReSort,
  handleClearAll,
  onAddTaskForList,
  isSuperfocused = false,
  onToggleSuperfocus,
  fullWidth = false,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `column-${list.id}`,
    data: { type: 'list-column', listId: list.id },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  // Drag handle — matches TaskCard's double GripVertical look.
  const dragHandle = activeListsCount > 1 && !isSuperfocused ? (
    <button
      ref={setActivatorNodeRef}
      {...listeners}
      type="button"
      className="no-hover cursor-grab active:cursor-grabbing flex flex-col gap-0 flex-shrink-0 px-1"
      title="Drag to reorder list"
      data-testid={`active-list-drag-${list.id}`}
      onClick={(e) => e.stopPropagation()}
    >
      <GripVertical size={20} className="text-muted-foreground -mb-2" />
      <GripVertical size={20} className="text-muted-foreground" />
    </button>
  ) : null;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={fullWidth ? 'flex-shrink-0 w-full' : COLUMN_SIZE}
      data-testid={`active-list-header-cell-${list.id}`}
    >
      <div
        className={`px-3 py-2 ${activeListsCount > 1 ? 'rounded-xl bg-slate-700' : ''}`}
        data-testid={`active-list-header-${list.id}`}
      >
        <div className="flex items-center gap-1">
          {dragHandlePosition === 'left' && dragHandle}
          <div className="flex-1 min-w-0">
            {renamingListId === list.id ? (
              <Input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={commitRenameList}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitRenameList();
                  if (e.key === 'Escape') {
                    setRenamingListId(null);
                    setRenameValue('');
                  }
                }}
                className="h-8 text-base font-semibold w-full"
                data-testid={`rename-list-input-${list.id}`}
              />
            ) : (
              <button
                type="button"
                onClick={() => startRenameList(list)}
                className="h-font text-lg font-semibold text-white hover:text-black hover:bg-slate-200 rounded px-1 transition-colors text-left w-full truncate"
                title="Click to rename"
                data-testid={`active-list-name-${list.id}`}
              >
                {list.name}
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({listTasks.length})
                </span>
              </button>
            )}
          </div>

          {onAddTaskForList && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onAddTaskForList(list)}
              className="h-8 w-8 flex-shrink-0 text-blue-500 hover:text-black hover:bg-[var(--color-secondary)]"
              title="Add task"
              data-testid={`active-list-add-task-${list.id}`}
            >
              <Plus size={24} strokeWidth={2.5} />
            </Button>
          )}

          {onToggleSuperfocus && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleSuperfocus}
              className={`hidden sm:inline-flex h-8 w-8 flex-shrink-0 hover:text-black hover:bg-[var(--color-secondary)] ${
                isSuperfocused ? 'text-[var(--color-primary)]' : 'text-blue-500'
              }`}
              title={isSuperfocused ? 'Exit Superfocus' : 'Superfocus'}
              data-testid={`active-list-superfocus-${list.id}`}
            >
              <Target size={18} />
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0 text-blue-500 hover:text-black hover:bg-[var(--color-secondary)]"
                title="Edit list"
                data-testid={`active-list-menu-${list.id}`}
              >
                <Pencil size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem
                onClick={() => startRenameList(list)}
                className={MENU_ITEM_HOVER}
                data-testid={`active-list-rename-${list.id}`}
              >
                Rename list
              </DropdownMenuItem>
              {activeListsCount > 1 && (
                <>
                  {handleAutoPopulate && (
                    <DropdownMenuItem
                      onClick={() => handleAutoPopulate(list.id)}
                      className={MENU_ITEM_HOVER}
                      data-testid={`active-list-auto-populate-${list.id}`}
                    >
                      Auto Fill
                    </DropdownMenuItem>
                  )}
                  {handleReSort && (
                    <DropdownMenuItem
                      onClick={() => handleReSort(list.id)}
                      className={MENU_ITEM_HOVER}
                      data-testid={`active-list-resort-${list.id}`}
                    >
                      Re-Sort
                    </DropdownMenuItem>
                  )}
                  {handleClearAll && (
                    <DropdownMenuItem
                      onClick={() => handleClearAll(list.id)}
                      className={MENU_ITEM_HOVER}
                      data-testid={`active-list-clear-all-${list.id}`}
                    >
                      Clear All
                    </DropdownMenuItem>
                  )}
                </>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setListToDelete(list)}
                disabled={activeListsCount <= 1}
                className={`text-red-400 focus:text-red-400 ${MENU_ITEM_HOVER}`}
                data-testid={`active-list-delete-${list.id}`}
              >
                <Trash2 size={14} className="mr-2" />
                Delete list
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {dragHandlePosition === 'right' && dragHandle}
        </div>
      </div>
    </div>
  );
}

/**
 * ListBodyCell
 * The sortable task list for a single list column.
 * Lives in the body row below the sticky headers strip.
 */
export function ListBodyCell({
  list,
  listTasks,
  focusMode,
  onTaskComplete,
  onTaskUpdate,
  tags,
  dragHandlePosition,
  onMoveToTop,
  onMoveToBottom,
  onMoveUp,
  onMoveDown,
  fullWidth = false,
}) {
  const visibleTasks = listTasks;

  return (
    <div
      className={fullWidth ? 'flex-shrink-0 w-full' : COLUMN_SIZE}
      data-testid={`active-list-body-cell-${list.id}`}
    >
      <SortableContext
        items={visibleTasks.map((t) => t.id)}
        strategy={verticalListSortingStrategy}
      >
        <DroppableListColumn listId={list.id} className="space-y-3 min-h-[120px] mt-3">
          {visibleTasks.length === 0 ? (
            <div
              className="rounded-xl p-6 text-center text-sm text-muted-foreground"
              style={{ backgroundColor: 'var(--color-secondary)' }}
              data-testid={`active-list-empty-${list.id}`}
            >
              No tasks in this list.
            </div>
          ) : (
            visibleTasks.map((task, index) => {
              let opacity = 1;
              let blur = 0;
              if (focusMode) {
                opacity = focusOpacity(index);
                blur = focusBlur(index);
              }
              return (
                <div
                  key={task.id}
                  style={
                    focusMode
                      ? {
                          opacity,
                          filter: `blur(${blur}px)`,
                          transition: 'all 0.3s ease-in-out',
                        }
                      : undefined
                  }
                >
                  <SortableTaskItem
                    task={task}
                    onComplete={onTaskComplete}
                    onUpdate={onTaskUpdate}
                    tags={tags}
                    dragHandlePosition={dragHandlePosition}
                    onMoveToTop={onMoveToTop}
                    onMoveToBottom={onMoveToBottom}
                    onMoveUp={onMoveUp}
                    onMoveDown={onMoveDown}
                    compact
                  />
                </div>
              );
            })
          )}
        </DroppableListColumn>
      </SortableContext>
    </div>
  );
}

export { SortableTaskItem };
